import React from "react";
import { DropdownOption, DropdownWithSearchProps } from ".";
import { useTranslation } from "react-i18next";
import useWindowOverflow from "hooks/useWindowOverflow";
import { slugify } from "utils";
import OptionItem from "./OptionItem";
import { IconChevronLeft, IconSearch } from "@tabler/icons-react";
import { Combobox, Transition } from "@headlessui/react";
import clsx from "clsx";
import NotFoundImg from "assets/images/icons/not-found.svg";
import ReactDOM from "react-dom";
import DebouncedInput from "components/DebounceInput";

interface OptionsMenu<T> extends DropdownWithSearchProps<T> {
  cursorPosition: { x: number; y: number };
  inputRef: React.RefObject<HTMLDivElement>;
  selectedItems: T[];
  setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>;
  closeMenu?: () => void;
  menuStyle?: React.CSSProperties;
}

function OptionsMenu<Item extends DropdownOption>(props: OptionsMenu<Item>) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { containerRef, getLeftPos } = useWindowOverflow();
  const [selectedCategory, setSelectedCategory] = React.useState<Item[]>([]);
  const [query, setQuery] = React.useState<string | number>("");
  const wrapperEl = document.getElementById(props.wrapperID as string);
  const optionsListRef = React.useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);

  const queryOption = {
    id: "search_term",
    name: query,
    name_de: query,
  } as Item;

  const optionItems = props.searchTermAsOption
    ? [queryOption, ...(props.items ?? [])]
    : props.items ?? [];

  React.useEffect(() => {
    setQuery(props.searchTerm ?? "");
    String(props.searchTerm).length && setFocusedIndex(0);
  }, [props.searchTerm]);

  React.useEffect(() => {
    const el = optionsListRef.current;
    if (props.optionsFocus && el) {
      el?.setAttribute("tabindex", "0");
      el?.focus();
      setFocusedIndex(String(props.searchTerm).length ? 1 : 0);
    }
  }, [props.optionsFocus]);

  const filteredItems: Item[] =
    query === "" ? props?.items ?? [] : filterWithQuery();

  function checkQueryExists(
    item: Item,
    filteredList: Item[],
    displayKey: keyof DropdownOption
  ) {
    const text = slugify(item[displayKey] as string);

    if ("children" in item && item.children?.length) {
      for (const child of item.children) {
        checkQueryExists(child as Item, filteredList, displayKey);
      }
    } else if (text.includes(slugify(query))) {
      filteredList.push(item);
    }
  }

  function filterWithQuery() {
    const filteredList: Item[] = [];
    const displayKey = props.displayKey as keyof DropdownOption;

    props?.items?.forEach((item) => {
      checkQueryExists(item, filteredList, displayKey);
    });

    return props.searchTermAsOption
      ? [queryOption, ...filteredList]
      : filteredList;
  }

  const handleOnSelect = (item: Item) => {
    if ((item as DropdownOption).children?.length) {
      const selected = [...selectedCategory, item];
      setSelectedCategory(selected);
    } else {
      const selected = props.selectedItems.find((rec) => rec.id === item.id)
        ? props.selectedItems.filter(
            (rec) => (rec as DropdownOption).id != (item as DropdownOption).id
          )
        : props.singleSelect
        ? [item]
        : [...props.selectedItems, item];
      props.setSelectedItems(selected);
      props.handleSelect?.(selected);
      props.closeMenu?.();
    }
  };

  function emptyState() {
    return (
      <div className="flex justify-center">
        <div className="w-[300px] flex flex-col -z-10 py-4 items-center justify-center">
          <img alt="not-found" src={NotFoundImg} className="pt-2" />
          <span className="font-normal text-sm text-gray-500 p-4 text-center whitespace-normal">
            {t("commons.onDropDownNoSearchResult")}
          </span>
        </div>
      </div>
    );
  }

  const onClickBack = () => {
    selectedCategory.splice(-1);
    setSelectedCategory([...selectedCategory]);
  };

  function showItems() {
    const childrenList = (selectedCategory.slice(-1)[0] as DropdownOption)
      ?.children;

    if (query) {
      return showSearchResults();
    }

    const itemsList =
      selectedCategory.length && query === "" ? childrenList : filteredItems;

    return itemsList?.map((item, i) => (
      <OptionItem
        key={i}
        item={item as Item}
        index={i}
        handleOnClick={handleOnSelect}
        detailKey={props.detailKey}
        query={query}
        selected={Boolean(
          props.selectedItems.find((rec) => rec.id === item.id)
        )}
        hasChildren={Boolean(item?.children?.length)}
        focusedIndex={focusedIndex}
        {...props}
      />
    ));
  }

  function categoryHeader(id: string | number | undefined, name: string) {
    return (
      <div
        key={`group-${id}`}
        className="bg-gray-50 py-1 px-4 text-gray-500 text-xs font-medium border-t border-b border-gray-300"
      >
        {name}
      </div>
    );
  }

  function showSearchResults() {
    let globalIndex = 0;

    const results: JSX.Element[] = [];
    const headers = new Set<string>();
    const queryTerm = slugify(query);

    function addCategoryHeader(grandparent: Item | null, parent: Item | null) {
      if (!parent || headers.has(String(parent.id))) {
        return;
      }
      headers.add(String(parent.id));
      if (grandparent && headers.has(String(grandparent.id))) {
        return;
      }

      let categoryID = parent.id;
      let categoryTitle = parent[props.displayKey] as string;

      if (grandparent && !headers.has(String(grandparent.id))) {
        categoryID = grandparent.id;
        categoryTitle = grandparent[props.displayKey] as string;
      }

      headers.add(String(categoryID));
      results.push(categoryHeader(categoryID, categoryTitle));
    }

    // search results will flatten a tree while filtering results based on whether the display key contains the term
    function flattenAndFilter(
      grandparent: Item | null,
      parent: Item | null,
      currentNode: Item
    ) {
      const isMatch =
        !!currentNode.column_key &&
        slugify(currentNode[props.displayKey] as string).includes(queryTerm);

      if (isMatch) {
        addCategoryHeader(grandparent, parent);
        results.push(
          <OptionItem
            key={currentNode.id}
            item={currentNode}
            index={globalIndex++}
            handleOnClick={handleOnSelect}
            detailKey={props.detailKey}
            query={query}
            selected={Boolean(
              props.selectedItems.find((rec) => rec.id === currentNode.id)
            )}
            //details={parent}
            focusedIndex={focusedIndex}
            {...props}
          />
        );
      }
      (currentNode.children || []).forEach((child) => {
        flattenAndFilter(parent, currentNode, child as Item);
      });
    }
    optionItems.forEach((item) => {
      flattenAndFilter(null, null, item);
    });

    return results;
  }

  function showSuggestedItems() {
    return (
      <div>
        {categoryHeader("suggested", t("mappingEditor.displayedColumns"))}
        {props.suggestedItems?.map((item, i) => (
          <OptionItem
            key={i}
            item={item}
            displayKey={props.displayKey}
            detailKey={props.detailKey}
            index={i}
            handleOnClick={handleOnSelect}
          />
        ))}
      </div>
    );
  }

  function searchField() {
    return (
      <div className="sticky top-0 p-4 z-10 bg-white">
        <div
          className={clsx(
            "bg-white border-gray-300 border rounded-md",
            "flex justify-between px-2 items-center text-gray-900"
          )}
        >
          <DebouncedInput
            className={clsx(
              "w-full pl-0 placeholder:text-gray-500 text-sm leading-5 ring-0 outline-none border-none focus:ring-0 rounded-md",
              props.searchClassName
            )}
            inputWrapperClassName="border-none"
            onChange={(value) => {
              setQuery(String(value));
            }}
            value={String(props.value ?? "")}
            placeholder={props.placeholder}
            icon={<></>}
          />
          <IconSearch width={24} className="text-gray-500" />
        </div>
      </div>
    );
  }

  function optionListItems() {
    if (!optionItems) return skeleton();
    return (
      <>
        {selectedCategory.length && !query ? (
          <div
            className="text-gray-500 flex items-center pl-2 pr-4 py-[6px] border-y border-gray-300 cursor-pointer bg-gray-50 group/menu relative"
            onClick={onClickBack}
          >
            <IconChevronLeft
              size={24}
              className="group-hover/menu:text-indigo-700"
            />
            <div className="text-center text-sm font-semibold w-full">
              {isEng
                ? selectedCategory?.slice(-1)[0]?.name
                : selectedCategory?.slice(-1)[0]?.name_de}
            </div>
          </div>
        ) : null}
        {filteredItems?.length === 0 && query !== ""
          ? emptyState()
          : showItems()}
        {shouldShowSuggestedItems() ? showSuggestedItems() : null}
      </>
    );
  }

  function shouldShowSuggestedItems() {
    return (
      props.suggestedItems?.length && query === "" && !selectedCategory.length
    );
  }

  function calcTopPosition() {
    return Math.round(
      Number(props.inputRef.current?.getBoundingClientRect().top) -
        Number(wrapperEl?.getBoundingClientRect().y) +
        Number(props.inputRef.current?.getBoundingClientRect().height)
    );
  }

  const menuStyle = wrapperEl
    ? {
        top: calcTopPosition(),
        left: Number(props.leftOffset),
      }
    : {
        left: props.fixedPos
          ? "inherit"
          : getLeftPos(props.cursorPosition.x, 20),
      };

  function handleScrollList(e: React.KeyboardEvent) {
    e.stopPropagation();
    if (e.code === "ArrowDown") {
      onArrowDown();
    } else if (e.code === "ArrowUp") {
      onArrowUp();
      focusedIndex === -1 && props.revertFocus?.();
    } else if (e.code === "Enter") {
      handleOnSelect(filteredItems[focusedIndex]);
    } else {
      props.revertFocus?.();
    }
  }

  function onArrowDown() {
    setFocusedIndex((prev) =>
      prev === filteredItems.length - 1 ? prev : prev + 1
    );
  }

  function onArrowUp() {
    setFocusedIndex((prev) => (prev <= 0 ? -1 : Number(prev) - 1));
  }

  function menuContent() {
    return (
      <Transition
        as={"div"}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        style={props.menuStyle ?? menuStyle}
        ref={containerRef}
        className={clsx(
          "absolute z-10 my-1 w-96",
          "max-h-[396px] overflow-auto rounded-md bg-white text-base !p-0 !max-w-fit",
          "shadow-lg border border-gray-300 min-w-auto w-auto max-w-md focus:outline-none sm:text-sm",
          props.optionsClassName,
          { "cursor-not-allowed": props.disabled }
        )}
      >
        <Combobox.Options
          ref={optionsListRef}
          onKeyDown={handleScrollList}
          autoFocus
          className="focus:outline-none"
        >
          {!props.labelWithSearch && !props.hideOptionsSearch
            ? searchField()
            : null}
          {optionListItems()}
        </Combobox.Options>
      </Transition>
    );
  }

  return wrapperEl
    ? ReactDOM.createPortal(menuContent(), wrapperEl)
    : menuContent();
}

export default OptionsMenu;

function skeleton() {
  return (
    <div className="animate-pulse px-3 pb-3">
      <div className="h-8 w-full bg-gray-200 rounded dark:bg-gray-700 mb-0.5"></div>
      <div className="h-8 w-full bg-gray-200 rounded dark:bg-gray-700 mb-0.5"></div>
    </div>
  );
}
